/**
 * Pre-market / stale-quote helpers.
 *
 * Background
 * ----------
 * During pre-market (or any time before today's regular session close is
 * available from our providers), the providers still return the *previous*
 * day's quote.  Its `time` is therefore NOT "today" (UTC).  When the user
 * switches back to the tab, the visibility-triggered /quote fetch would
 * overwrite the correct DB candle with that stale provider snapshot.
 *
 * The rule encoded here:
 *   - quote.time is today (UTC)  -> provider is current -> use it
 *   - quote.time is older        -> provider is stale    -> prefer the
 *     latest DB daily candle IF that candle is from the same UTC day or
 *     newer than the quote's day.
 *
 * The decision is a pure function (no `Date.now()` dependency beyond an
 * explicit `now` argument) so it is easy to unit test.
 */

/**
 * Parse a timestamp in any common shape into epoch seconds (UTC), or null.
 * Supports:
 *   - number (epoch seconds)
 *   - ISO-8601 string ("2026-08-28T14:30:00Z")
 *   - date-only string ("2026-08-28") → UTC midnight
 */
export function parseTsSec(value) {
  try {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const s = value.trim();
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) {
        return Math.floor(
          Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 1000
        );
      }
      const t = Date.parse(s);
      if (!Number.isNaN(t)) return Math.floor(t / 1000);
    }
    if (value && typeof value === 'object') {
      if (value.$date != null) return parseTsSec(value.$date);
      if ('year' in value) {
        return Math.floor(
          Date.UTC(value.year, (value.month || 1) - 1, value.day || 1) / 1000
        );
      }
    }
  } catch {
    /* fall through */
  }
  return null;
}

/**
 * Extract a numeric epoch-seconds timestamp from a bar/quote object that may
 * carry `time` (seconds) or `date` (ISO string). Returns null when absent.
 */
export function barTsSec(bar) {
  if (!bar) return null;
  return parseTsSec(bar.time ?? bar.date);
}

/**
 * Determine the US (NYSE) market session for a given instant.
 *
 * Boundaries (America/New_York wall clock):
 *   - before 09:30  -> 'PRE'
 *   - 09:30 – 16:00 -> 'REGULAR'
 *   - 16:00 – 20:00 -> 'POST'
 *   - other weekdays 20:00 – 04:00 (approx) and weekends -> 'CLOSED'
 *
 * Holidays are NOT considered (consistent with the rest of the app).
 *
 * @param {number|Date|undefined} nowMs milliseconds since epoch
 * @returns {'PRE'|'REGULAR'|'POST'|'CLOSED'}
 */
export function getUsSession(nowMs) {
  try {
    const t = typeof nowMs === 'number' && Number.isFinite(nowMs) ? nowMs : Date.now();
    const nyStr = new Date(t).toLocaleString('en-US', { timeZone: 'America/New_York' });
    const ny = new Date(nyStr);
    const day = ny.getDay(); // 0=Sun..6=Sat
    if (day === 0 || day === 6) return 'CLOSED';
    const mins = ny.getHours() * 60 + ny.getMinutes();
    if (mins < 9 * 60 + 30) return 'PRE';
    if (mins < 16 * 60) return 'REGULAR';
    if (mins < 20 * 60) return 'POST';
    return 'CLOSED';
  } catch {
    return 'CLOSED';
  }
}

/**
 * Decide whether a /quote payload should be applied as a live intraday
 * update on the chart (vs. keeping/showing the latest DB daily candle).
 *
 * Rules (in priority order):
 *   1. quote.time is today (UTC) and isMarketOpen === true        -> true  (LIVE)
 *   2. quote.time is today (UTC) with usable extended-session data (session
 *      PRE/POST + finite extendedPrice)                            -> true
 *   3. quote.time is today (UTC), market is CLOSED, and no
 *      extended data                                              -> false
 *      (providers stamp the last completed daily session with a
 *      fresh "today" timestamp; applying it would corrupt today's
 *      candle on the chart)
 *   4. otherwise, whenever the provider returned a usable time    -> true
 *
 * Pure function so it can be unit tested in isolation.
 *
 * @param {object}  quote    normalized quote from /quote ({ time, close, ... })
 * @param {string}  session  current US market session: 'PRE'|'REGULAR'|'POST'|'CLOSED'
 * @param {object}  [opts]
 * @param {number}  [opts.nowMs] milliseconds since epoch (default Date.now())
 * @returns {boolean}
 */
export function shouldApplyLiveQuote(quote, session, opts = {}) {
  if (!quote || typeof quote.time === 'undefined') return false;
  const nowMs =
    typeof opts.nowMs === 'number' && Number.isFinite(opts.nowMs)
      ? opts.nowMs
      : Date.now();
  const qTs = parseTsSec(quote.time);
  if (qTs == null || !Number.isFinite(qTs)) return false; // unusable timestamp
  const isUtcSameDay = (tsSec) => {
    if (!Number.isFinite(tsSec)) return false;
    const d = new Date(nowMs);
    const start = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 1000;
    return tsSec >= start && tsSec < start + 86400;
  };
  const qIsToday = isUtcSameDay(qTs);
  const sess = String(quote.session || '').toUpperCase();
  const extP =
    typeof quote.extendedPrice === 'number' && Number.isFinite(quote.extendedPrice)
      ? quote.extendedPrice
      : null;
  const isMarketOpen = quote.isMarketOpen === true;

  if (qIsToday && isMarketOpen) return true;
  const hasTodayExtended = qIsToday && (sess === 'PRE' || sess === 'POST') && extP != null;
  if (hasTodayExtended) return true;
  // Stale daily-session snapshot stamped "today" while the market is closed.
  if (qIsToday && !isMarketOpen && !hasTodayExtended && session === 'CLOSED') return false;
  return true;
}

/**
 * Decide whether a provider quote should be replaced by the latest DB candle.
 *
 * @param {object}  quote     normalized quote from /quote ({ time, close, ... })
 * @param {object}  dbCandle  latest daily candle from our DB ({ time (sec), close }), or null
 * @param {object}  [opts]
 * @param {number}  [opts.nowMs]   milliseconds since epoch (default Date.now())
 * @param {number}  [opts.nowSec]  seconds since epoch; overrides nowMs if given
 * @returns {{action: 'use-provider'|'use-db', reason: string}}
 */
export function resolveQuoteDecision(quote, dbCandle, opts = {}) {
  const nowSec =
    typeof opts.nowSec === 'number' && Number.isFinite(opts.nowSec)
      ? opts.nowSec
      : Math.floor(
          (typeof opts.nowMs === 'number' && Number.isFinite(opts.nowMs) ? opts.nowMs : Date.now()) / 1000
        );

  const quoteTs = parseTsSec(quote?.time ?? quote?.date);
  const dbTs = barTsSec(dbCandle);

  const utcDayStart = (tsSec) => {
    const d = new Date(tsSec * 1000);
    return Math.floor(
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 1000
    );
  };

  const nowDay = utcDayStart(nowSec);
  const quoteIsToday = quoteTs != null && utcDayStart(quoteTs) === nowDay;

  // Provider reports a current-session quote → always trust it.
  if (quoteIsToday) {
    return { action: 'use-provider', reason: 'quote-is-today' };
  }

  // Stale quote (previous day or older).  Prefer the DB candle when it
  // represents the same UTC day or a newer one than the quote's day.
  if (dbTs != null) {
    const dbDay = utcDayStart(dbTs);
    const quoteDay = quoteTs != null ? utcDayStart(quoteTs) : -Infinity;
    if (dbDay >= quoteDay) {
      return { action: 'use-db', reason: 'stale-quote-db-is-fresher-or-same' };
    }
    // DB is older than the stale quote → keep the provider value.
    return { action: 'use-provider', reason: 'db-older-than-quote' };
  }

  // No DB candle available → nothing better to show.
  return { action: 'use-provider', reason: 'no-db-candle' };
}

/**
 * Compute the header "display close" for a stock detail page.
 *
 * Pure function so it can be unit tested in isolation.  Precedence:
 *   1. quote is today AND market open            -> quote.close        (LIVE)
 *   2. quote is today AND extended session       -> extendedPrice      (PRE/POST)
 *   3. quote is today (no extended data)         -> quote.close        (TODAY)
 *   4. quote stale (pre-market) AND DB last bar  -> last.close         (PREV)
 *   5. final fallback                            -> last.close         (PREV/TODAY)
 *
 * @param {object}  quote      normalized quote or null
 * @param {Array}   historical daily candles from DB, ascending by time
 * @param {object}  [opts]
 * @param {number}  [opts.nowMs]
 * @returns {{displayClose: number|null, badge: string|null, pct: number|null}}
 */
export function computeHeaderPrice(quote, historical, opts = {}) {
  const nowMs =
    typeof opts.nowMs === 'number' && Number.isFinite(opts.nowMs) ? opts.nowMs : Date.now();

  const isUtcSameDay = (tsSec) => {
    if (!Number.isFinite(tsSec)) return false;
    const d = new Date(nowMs);
    const todayStartSec =
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 1000;
    return tsSec >= todayStartSec && tsSec < todayStartSec + 86400;
  };

  const rows = Array.isArray(historical) ? historical : [];
  const last = rows.length ? rows[rows.length - 1] : null;
  const prev = rows.length > 1 ? rows[rows.length - 2] : null;

  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
  const lastTs = parseTsSec(last?.time ?? last?.date);
  const lastClose = num(last?.close ?? last?.close_price);
  const prevClose = num(prev?.close ?? prev?.close_price);

  const qClose = num(quote?.close);
  const qTs = num(quote?.time);
  const qIsToday = isUtcSameDay(qTs || 0);
  const sess = String(quote?.session || '').toUpperCase();
  const extP =
    typeof quote?.extendedPrice === 'number' && Number.isFinite(quote.extendedPrice)
      ? quote.extendedPrice
      : null;

  const decision = resolveQuoteDecision(quote, last, { nowMs });
  const stalePreferDb = decision.action === 'use-db';

  let displayClose = null;
  let badge = null;

  const session = getUsSession(nowMs);

  // 1) LIVE — only during the actual regular session.  Some providers report
  // the previous day's close with a *fresh* timestamp during pre-market
  // (isMarketOpen would be true), which must not be displayed as live.
  if (qClose != null && qIsToday && quote?.isMarketOpen && session === 'REGULAR') {
    displayClose = qClose;
    badge = 'LIVE';
  }

  // 2) Extended session
  if (displayClose == null && qIsToday && !quote?.isMarketOpen) {
    if (sess === 'PRE' && extP != null) {
      displayClose = extP;
      badge = 'PRE';
    } else if (sess === 'POST' && extP != null) {
      displayClose = extP;
      badge = 'POST';
    } else if (qClose != null && !stalePreferDb) {
      // Market closed, no extended data, and the DB does NOT have a
      // same-day-or-newer candle: the provider quote is the freshest data.
      displayClose = qClose;
      badge = 'TODAY';
    }
    // If stalePreferDb is true we deliberately skip the provider's
    // (stale) close and let the DB candle win below.
  }

  // 3) Stale (pre-market) quote → prefer latest DB HLOC
  if (displayClose == null && stalePreferDb && lastClose != null) {
    displayClose = lastClose;
    badge = isUtcSameDay(lastTs || 0) ? 'TODAY' : 'PREV';
  }

  // 4) Final fallback: latest DB close
  if (displayClose == null && lastClose != null) {
    displayClose = lastClose;
    badge = isUtcSameDay(lastTs || 0) ? 'TODAY' : 'PREV';
  }

  // Percent change and absolute change
  let pct = null;
  let change = null;
  const qOpen = num(quote?.open);

  if (badge === 'LIVE' && quote) {
    if (qOpen != null && qClose != null && qOpen !== 0) {
      change = qClose - qOpen;
      pct = (change / qOpen) * 100;
    }
  } else if ((badge === 'PRE' || badge === 'POST') && quote) {
    if (
      typeof quote.extendedChangePercent === 'number' &&
      Number.isFinite(quote.extendedChangePercent)
    ) {
      pct = quote.extendedChangePercent;
      if (extP != null && qClose != null) change = extP - qClose;
    } else if (extP != null && qClose != null && qClose !== 0) {
      change = extP - qClose;
      pct = (change / qClose) * 100;
    }
  } else {
    // Historical / DB based: last close vs previous close
    if (lastClose != null && prevClose != null && prevClose !== 0) {
      change = lastClose - prevClose;
      pct = (change / prevClose) * 100;
    }
  }

  return { displayClose, badge, pct, change };
}
